import { useIdle } from '@vueuse/core';
import { defineStore } from 'pinia';
import {
  SessionCoordinatorMessage,
  SessionCoordinatorMessageType,
  SessionCoordinatorResponseValue,
} from '~~/data/models/SessionCoordination';
import { debounce } from 'lodash';
import { useModal } from './modal';
import IdleTimeOutModal from '@/components/IdleTimeOutModal.vue';

/*
  Session Timeout
    - This coordinates between tabs opened, checking whether active/idle.
    - Uses a BroadcastChannel to achieve this: https://developer.mozilla.org/en-US/docs/Web/API/Broadcast_Channel_API
    - Flow of communication can be seen at sessionCoordinatorBC.onmessage
    - Most exports are for debugging purposes only, but resetOnUserDismissTimeoutModal is used at the modal.

    Presuming certain questions:
      - Why use a store? Because the timeout modal needs to share state with whatever component this is initially mounted on (app.vue) - in my tests, composables couldn't do this.
      - Why so much logic? BroadcastChannel messages can't contain callbacks - so need to run immediate execution, whilst broadcasting need for execution on other tabs. 

    If considering refactor:
      - Keep Lodash debounce (or equivalent); intervals will cause a pile-up and eat memory.
*/

export const useSessionTimeout = defineStore('useSessionTimeout', () => {
  // #region Setup
  const twentyfiveMinutes = 25 * 60 * 1000;
  const { idle, reset } = useIdle(twentyfiveMinutes);

  const modal = useModal();
  const isTimeoutModalActive = ref(false);

  const sessionCoordinatorBC = new BroadcastChannel('SessionCoordinator');
  const responses = ref([]) as Ref<SessionCoordinatorMessage[]>;

  function storeResponse(message: SessionCoordinatorMessage): void {
    responses.value.push(message);
  }

  function clearResponses(): void {
    responses.value = [];
  }

  sessionCoordinatorBC.onmessage = (event) => {
    const message = event.data as SessionCoordinatorMessage;

    switch (message.type) {
      case SessionCoordinatorMessageType.QUERY_TABS:
        replyToQueryWithActivityStatus();
        break;
      case SessionCoordinatorMessageType.RESPONSE:
        storeResponseAndEvaluateResponses(message);
        break;
      case SessionCoordinatorMessageType.SHOW_MODAL:
        showTimeoutModal();
        break;
      case SessionCoordinatorMessageType.RESET:
        resetOnUserBecomingActive();
        break;
    }
  };
  // #endregion

  // #region QueryTabs
  const broadcastNewQueryDebounced = debounce(() => {
    if (!idle.value) {
      return;
    }
    broadcastNewQuery();
  }, 30000);

  function broadcastNewQuery(): void {
    clearResponses();
    sessionCoordinatorBC.postMessage(
      new SessionCoordinatorMessage(SessionCoordinatorMessageType.QUERY_TABS)
    );
    evaluateResponsesDebounced();
  }

  function replyToQueryWithActivityStatus(): void {
    sessionCoordinatorBC.postMessage(
      new SessionCoordinatorMessage(
        SessionCoordinatorMessageType.RESPONSE,
        idle.value ? SessionCoordinatorResponseValue.IDLE : SessionCoordinatorResponseValue.ACTIVE
      )
    );
  }
  // #endregion

  // #region Response
  function storeResponseAndEvaluateResponses(message: SessionCoordinatorMessage): void {
    storeResponse(message);
    evaluateResponsesDebounced();
  }

  const evaluateResponsesDebounced = debounce(() => {
    if (!idle.value) {
      clearResponses();
      return;
    }

    const isAnyActiveTab = responses.value.some(
      (response) => response.value === SessionCoordinatorResponseValue.ACTIVE
    );

    if (isAnyActiveTab) {
      clearResponses();
      broadcastNewQueryDebounced();
      return;
    }

    showTimeoutModal();
    broadcastShowTimeoutModal();
  }, 5000);

  function broadcastShowTimeoutModal(): void {
    sessionCoordinatorBC.postMessage(
      new SessionCoordinatorMessage(SessionCoordinatorMessageType.SHOW_MODAL)
    );
  }
  // #endregion

  // #region ShowModal
  function showTimeoutModal(): void {
    isTimeoutModalActive.value = true;
    modal.open({
      component: shallowRef(IdleTimeOutModal),
    });
  }

  function closeTimeoutModal(): void {
    if (!isTimeoutModalActive.value) {
      return;
    }
    isTimeoutModalActive.value = false;
    modal.close();
  }
  // #endregion

  // #region Reset
  function resetOnUserBecomingActive(): void {
    clearResponses();
    reset();
    closeTimeoutModal();
  }

  function broadcastReset(): void {
    sessionCoordinatorBC.postMessage(
      new SessionCoordinatorMessage(SessionCoordinatorMessageType.RESET)
    );
  }

  function resetOnUserDismissTimeoutModal(): void {
    resetOnUserBecomingActive();
    broadcastReset();
  }
  // #endregion

  watch(
    () => idle.value,
    (newValue, oldValue) => {
      if (newValue === oldValue || !newValue) {
        return;
      }

      broadcastNewQuery();
    }
  );

  // These values are mostly for debugging.
  return {
    responses: computed(() => responses),
    idle: computed(() => idle),
    reset: () => reset(),
    isTimeoutModalActive: computed(() => isTimeoutModalActive),
    resetOnUserDismissTimeoutModal: () => resetOnUserDismissTimeoutModal(),
  };
});
